import React from 'react';
import { Users, ChevronLeft, ChevronRight } from 'lucide-react';
import LeadRow from './LeadRow.jsx';

export default function LeadsTable({
  leads = [],
  total = 0,
  page = 1,
  pageSize = 50,
  totalPages = 1,
  isLoading = false,
  selected = new Set(),
  allMatching = false,
  selectingAll = false,
  toggleSelectAll,
  toggleSelect,
  clearSelection,
  selectAllMatching,
  setPage,
  dispositions = [],
  drips = [],
  onOpenLead,
  onDispositionApplied,
}) {
  return (
    <div className="panel p-0 overflow-hidden mt-4">
      {/* Header row */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-white/10 bg-white/3">
        <input
          type="checkbox"
          checked={leads.length > 0 && leads.every((l) => selected.has(l._id))}
          onChange={toggleSelectAll}
          className="w-4 h-4 accent-civix-purple cursor-pointer"
        />
        <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold flex-1">
          All ({total.toLocaleString()})
        </span>
        <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">
          {isLoading ? 'Loading…' : `Page ${page} / ${totalPages}`}
        </span>
      </div>

      {allMatching && selected.size > 0 ? (
        <div className="flex items-center justify-center gap-2 px-4 py-2 text-xs bg-civix-purple/15 border-b border-civix-purple/30">
          <span className="text-white/80">
            All <strong>{selected.size.toLocaleString()}</strong> leads matching this filter are selected.
          </span>
          <button onClick={clearSelection} className="text-civix-purpleSoft underline hover:text-white cursor-pointer">
            Clear selection
          </button>
        </div>
      ) : (
        leads.length > 0 &&
        leads.every((l) => selected.has(l._id)) &&
        total > leads.length && (
          <div className="flex items-center justify-center gap-2 px-4 py-2 text-xs bg-white/4 border-b border-white/10">
            <span className="text-white/70">
              All <strong>{leads.length}</strong> on this page are selected.
            </span>
            <button
              onClick={selectAllMatching}
              disabled={selectingAll}
              className="text-civix-purpleSoft underline hover:text-white disabled:opacity-50 cursor-pointer"
            >
              {selectingAll ? 'Selecting…' : `Select all ${total.toLocaleString()} matching this filter`}
            </button>
          </div>
        )
      )}

      {/* Empty state */}
      {!isLoading && leads.length === 0 && (
        <div className="p-12 text-center">
          <Users className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/60">No leads match your filters.</p>
          <p className="text-xs text-white/40 mt-1">Adjust filters above, or import leads with the buttons up top.</p>
        </div>
      )}

      {/* Lead rows */}
      <div className="divide-y divide-white/5">
        {leads.map((lead) => (
          <LeadRow
            key={lead._id}
            lead={lead}
            dispositions={dispositions}
            drips={drips}
            checked={selected.has(lead._id)}
            onCheck={() => toggleSelect(lead._id)}
            onOpen={() => onOpenLead(lead._id)}
            onDispositionApplied={onDispositionApplied}
          />
        ))}
      </div>

      {/* Pagination footer */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-white/2">
          <span className="text-xs text-white/50">
            Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
              className="p-1.5 rounded-sm hover:bg-white/5 disabled:opacity-30 cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs px-2">
              {page} / {totalPages}
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              className="p-1.5 rounded-sm hover:bg-white/5 disabled:opacity-30 cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
